/**
 * An image or PDF in the message list. 150 px high; 220 px wide alone and
 * 150 px in a group, so two fit side by side at 390 px.
 */

import { Download, Loader2 } from 'lucide-react';

import type { AttachmentUrl } from '../../hooks/chat/useAttachmentUrls';
import type { AttachmentOut } from '../../services/api';
import { formatFileSize } from '../../utils/attachments';

interface AttachmentTileProps {
  attachment: AttachmentOut;
  /** undefined: not fetched yet. null: outside the caller's channels. */
  url: AttachmentUrl | null | undefined;
  alone?: boolean;
  onOpen?: () => void;
  onDownload: (id: string) => void;
}

export function AttachmentTile({
  attachment,
  url,
  alone = false,
  onOpen,
  onDownload,
}: AttachmentTileProps) {
  const ready = attachment.status === 'ready';
  const previewUrl = ready ? url?.previewUrl : undefined;
  // Spinner only while the server is still rendering. A ready tile whose URL
  // is loading, and an id the caller cannot reach, get the bare placeholder.
  const preparing = !ready && url !== null;
  const width = alone ? 'w-[220px]' : 'w-[150px]';

  return (
    <div className={`${width} max-w-full overflow-hidden rounded-md border`}>
      {previewUrl ? (
        <button
          type='button'
          aria-label={`Open ${attachment.file_name}`}
          className='relative block h-[150px] w-full'
          onClick={onOpen}
        >
          <img
            src={previewUrl}
            alt={attachment.file_name}
            // A PDF page's text starts at the top; the centre crop is blank.
            className={`h-[150px] w-full object-cover ${attachment.content_type === 'application/pdf' ? 'object-top' : ''}`}
          />
          {attachment.page_count ? (
            <span className='absolute left-1 top-1 rounded bg-black/60 px-1 text-xs text-white'>
              {attachment.page_count} pages
            </span>
          ) : null}
        </button>
      ) : (
        <div className='flex h-[150px] w-full items-center justify-center gap-2 bg-muted'>
          {preparing && (
            <>
              <Loader2
                data-testid='tile-spinner'
                className='h-4 w-4 animate-spin'
                aria-hidden
              />
              <p className='text-xs text-muted-foreground'>Preparing preview</p>
            </>
          )}
        </div>
      )}
      <div className='flex items-center gap-1 px-2 py-1'>
        <p className='min-w-0 flex-1 truncate text-xs'>
          {attachment.file_name}
        </p>
        <span className='whitespace-nowrap text-xs text-muted-foreground'>
          {formatFileSize(attachment.size_bytes)}
        </span>
        <button
          type='button'
          aria-label={`Download ${attachment.file_name}`}
          onClick={() => onDownload(attachment.id)}
        >
          <Download className='h-3.5 w-3.5' />
        </button>
      </div>
    </div>
  );
}
